import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocationInfo, supabaseLocationService } from '../../services/supabaseLocationService';
import { supabaseClient } from '../../services/supabase';
import { OVERVIEW_SECTIONS } from '../../config/adminOverviewConfig';
import { ADMIN_PAGES } from '../../config/adminPageConfig';
import { LocationSelector } from '../LocationSelector';
import { HeroSlideshow } from '../layout/HeroSlideshow';
import { PageHeader } from '../layout/PageHeader';
import { useLocation } from '../../contexts/LocationContext';
import { BurdenBenefitsChart } from '../charts/BurdenBenefitsChart';
import { fetchElectricitySeries, fetchNaturalGasSeries } from '../charts/EnergyUseSupabase';
import { fetchGHGInventoryData } from '../charts/GHGInventorySupabase';
import { DataService } from '../../services/dataService';
import { VMTChart } from '../charts/VMTChart';
import { TransportationModeShare } from '../charts/TransportationModeShare';
import EVFleetAdoption from '../charts/EVFleetAdoption';
import { ChartRendererSupabase } from '../charts/ChartRendererSupabase';
import WasteTonnageChart from '../charts/WasteTonnageChart';
import LandfillEmissionsStackedChart from '../charts/LandfillEmissionsStackedChart';
import IncomeByRaceBar from '../charts/IncomeByRaceBar';
import HousingBurdenSection from '../charts/HousingBurdenSection';
import { CumulativeSolarCapacityChart } from '../charts/CumulativeSolarCapacityChart';
import { SectorBasedGHGInventory } from '../charts/SectorBasedGHGInventory';
import { ConsumptionBasedGHGInventory } from '../charts/ConsumptionBasedGHGInventory';
import { fetchConsumptionBasedGHGData } from '../../services/consumptionBasedGHGService';
import DataDownloadModal from '../auth/DataDownloadModal';
import { CustomSectionsForPage } from '../customContent/CustomSectionsForPage';
import { CustomDatasetsForPage } from '../customContent/CustomDatasetsForPage';
import { GLOBAL_ADMIN_LOCATION } from '../../constants/adminSettings';
import { useUserRole } from '../../contexts/UserRoleContext';

import './Overview.css';

// Extend Window interface for Overview loading state
declare global {
  interface Window {
    overviewFullyLoaded?: boolean;
  }
}

// Function to calculate stacked data as sum of residential + commercial (same as Energy page)
const calculateStackedData = (resData: any, comData: any, type: string) => {
  if (!resData?.chartData || !comData?.chartData) return null;

  const resLabels = (resData.chartData.labels || []) as number[];
  const comLabels = (comData.chartData.labels || []) as number[];
  
  // Get years that exist in both residential and commercial data
  const resYears = new Set(resLabels);
  const comYears = new Set(comLabels);
  const allYears: number[] = Array.from(resYears).filter((year: number) => comYears.has(year)).sort();
  
  // Extract data from all datasets (observed, interpolated, extrapolated) for each sector
  const resDatasets = resData.chartData.datasets || [];
  const comDatasets = comData.chartData.datasets || [];
  
  // Create maps for each sector's total values (sum of all data types)
  const resMap = new Map<number, number>();
  const comMap = new Map<number, number>();
  
  // Sum up all data types for residential
  resLabels.forEach((year: number, i: number) => {
    let total = 0;
    resDatasets.forEach((dataset: any) => {
      total += (dataset.data?.[i] || 0);
    });
    resMap.set(year, total);
  });
  
  // Sum up all data types for commercial
  comLabels.forEach((year: number, i: number) => {
    let total = 0;
    comDatasets.forEach((dataset: any) => {
      total += (dataset.data?.[i] || 0);
    });
    comMap.set(year, total);
  });

  // Create stacked chart data
  const stackedChartData = {
    labels: allYears,
    datasets: [
      {
        label: 'Residential',
        data: allYears.map(year => resMap.get(year) || 0),
        backgroundColor: type === 'Electricity' ? '#1e40af' : '#166534',
        borderColor: type === 'Electricity' ? '#1e40af' : '#166534',
        borderWidth: 1,
        stack: 'total'
      },
      {
        label: 'Commercial',
        data: allYears.map(year => comMap.get(year) || 0),
        backgroundColor: type === 'Electricity' ? '#3b82f6' : '#22c55e',
        borderColor: type === 'Electricity' ? '#3b82f6' : '#22c55e',
        borderWidth: 1,
        stack: 'total'
      }
    ]
  };

  // Create chart options for stacked bar chart
  const stackedChartOptions = {
    type: 'bar',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        position: 'bottom',
        labels: {
          padding: 10,
          usePointStyle: true,
          pointStyle: 'rect'
        }
      },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const v = Number(ctx.parsed.y);
            return `${ctx.dataset.label}: ${Number.isFinite(v) ? v.toLocaleString() : v}`;
          }
        }
      }
    },
    scales: {
      x: { 
        stacked: true, 
        title: { display: true, text: 'Year' } 
      },
      y: { 
        stacked: true, 
        beginAtZero: true, 
        title: { 
          display: true, 
          text: type === 'Electricity' ? 'kWh' : 'Therms'
        } 
      }
    }
  };

  return {
    chartData: stackedChartData,
    chartOptions: stackedChartOptions
  };
};

interface OverviewProps {
  onLocationSelect: (location: string) => void;
  isLandingPage?: boolean;
}

interface LivabilityRankingRow {
  rank: number;
  locationName: string;
  geofips: string;
  score: number;
  burdens: number;
  benefits: number;
}

type RankingRowCount = 5 | 10 | 25 | 50 | 100 | 'all';

interface LivabilityRankingSourceRow {
  location_name: string | null;
  geofips: string | null;
  year: number | null;
  total_burdens_percentile: number | null;
  total_benefits_percentile: number | null;
}

interface LivabilityTrendRow {
  year: number;
  total_score_percentile: number | null;
  total_burdens_percentile: number | null;
  total_benefits_percentile: number | null;
}

export const Overview: React.FC<OverviewProps> = ({ onLocationSelect, isLandingPage = false }) => {
  const { selectedLocation, selectedGeofips } = useLocation();
  const navigate = useNavigate();
  const { hasAnyRole } = useUserRole();

  // Helper function to handle navigation with loading check
  const handleNavigateWithCheck = (path: string) => {
    const slug = selectedLocation ? supabaseLocationService.getSlugForLocation(selectedLocation) : null;
    const targetPath = slug ? `/${slug}${path}` : path;

    // Always allow navigation to Transportation page
    if (path === '/transportation') {
      navigate(targetPath);
      // Scroll to top after navigation
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
      return;
    }
    
    // Check if Overview is fully loaded before allowing navigation to other pages
    if (!overviewFullyLoaded) {
      alert('Please wait until the Overview page fully loads before navigating to other pages. The Transportation page is available.');
      return;
    }
    
    navigate(targetPath);
    // Scroll to top after navigation
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };
  const [availableLocations, setAvailableLocations] = useState<LocationInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [acceptCommunications, setAcceptCommunications] = useState(true);
  const [elecStacked, setElecStacked] = useState<any>(null);
  const [gasStacked, setGasStacked] = useState<any>(null);
  const [elecChangeSince2005, setElecChangeSince2005] = useState<number | null>(null);
  const [gasChangeSince2005, setGasChangeSince2005] = useState<number | null>(null);
  const [cumulativeSolarCapacity, setCumulativeSolarCapacity] = useState<number | null>(null);
  const [ghgInventoryData, setGhgInventoryData] = useState<any>(null);
  const [consumptionGHGData, setConsumptionGHGData] = useState<any>(null);
  const [ghgChartType, setGhgChartType] = useState<'stacked-area' | 'line' | 'pie'>('stacked-area');
  const [consumptionChartType, setConsumptionChartType] = useState<'stacked-area' | 'line' | 'pie'>('stacked-area');
  const [housingBurdenPercentile, setHousingBurdenPercentile] = useState<number | null>(null);
  const [incomeDifferenceRatio, setIncomeDifferenceRatio] = useState<number | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showDataDownload, setShowDataDownload] = useState(false);
  const [vmtChangeSince2005, setVmtChangeSince2005] = useState<number | null>(null);
  const [privateVehiclePercent, setPrivateVehiclePercent] = useState<number | null>(null);
  const [evPercent, setEvPercent] = useState<number | null>(null);
  const [wasteChangeSince2005, setWasteChangeSince2005] = useState<number | null>(null);
  const [landfillEmissions2023, setLandfillEmissions2023] = useState<number | null>(null);
  const [burdenPercentile, setBurdenPercentile] = useState<number | null>(null);
  const [benefitsPercentile, setBenefitsPercentile] = useState<number | null>(null);
  const [vmtDataReady, setVmtDataReady] = useState<boolean>(false);
  const [vmtDataAvailable, setVmtDataAvailable] = useState<boolean>(false);
  const [overviewVMTChartLoaded, setOverviewVMTChartLoaded] = useState<boolean>(false);
  const [overviewFullyLoaded, setOverviewFullyLoaded] = useState<boolean>(false);
  const [overviewSettings, setOverviewSettings] = useState<Record<string, Record<string, boolean>> | null>(null);
  const [overviewVisibilitySettings, setOverviewVisibilitySettings] = useState<Record<string, Record<string, string[] | null>> | null>(null);
  const [overviewSettingsLoaded, setOverviewSettingsLoaded] = useState<boolean>(false);
  const [rankingGeographyType, setRankingGeographyType] = useState<'City' | 'County'>('City');
  const [rankingYear, setRankingYear] = useState<number>(2023);
  const [rankingAvailableYears, setRankingAvailableYears] = useState<number[]>([]);
  const [rankingRowCount, setRankingRowCount] = useState<RankingRowCount>(10);
  const [rankingTopRows, setRankingTopRows] = useState<LivabilityRankingRow[]>([]);
  const [rankingBottomRows, setRankingBottomRows] = useState<LivabilityRankingRow[]>([]);
  const [rankingLoading, setRankingLoading] = useState<boolean>(false);
  const [rankingError, setRankingError] = useState<string | null>(null);
  const [livabilityTrendRows, setLivabilityTrendRows] = useState<LivabilityTrendRow[]>([]);
  const [livabilityTrendLoading, setLivabilityTrendLoading] = useState<boolean>(false);
  const [livabilityTrendError, setLivabilityTrendError] = useState<string | null>(null);
  const rankingDataCacheRef = useRef<Partial<Record<'City' | 'County', LivabilityRankingSourceRow[]>>>({});
  const livabilityTrendCacheRef = useRef<Record<string, LivabilityTrendRow[]>>({});
  const ghgInventoryLoadingRef = useRef<boolean>(false); // Prevent multiple simultaneous GHG Inventory loads
  const ghgInventoryLoadedForLocationRef = useRef<string | null>(null); // Track which location has GHG Inventory loaded
  const vmtChartDataLoadedRef = useRef<boolean>(false); // Track if VMT chart has loaded data for current location
  const overviewLoadStartRef = useRef<number | null>(null);
  const [overviewLoadElapsedSec, setOverviewLoadElapsedSec] = useState<number>(0);
  const TYPICAL_OVERVIEW_LOAD_SEC = 22;

  // Memoize chart callbacks so chart components (e.g. WasteTonnageChart) don't rebuild on every Overview re-render during load
  const onBurdenPercentileCalculated = React.useCallback((percentile: number | null) => setBurdenPercentile(percentile), []);
  const onBenefitsPercentileCalculated = React.useCallback((percentile: number | null) => setBenefitsPercentile(percentile), []);
  const onVmtMetricsCalculated = React.useCallback((metrics: { changeSince2005?: number }) => setVmtChangeSince2005(metrics.changeSince2005 ?? null), []);
  const onWasteChangeCalculated = React.useCallback((change: number | null) => setWasteChangeSince2005(change), []);
  const onModeShareCalculated = React.useCallback((data: { privateAuto?: number }) => setPrivateVehiclePercent(data.privateAuto ?? null), []);
  const onEvPercentCalculated = React.useCallback((percent: number | null) => setEvPercent(percent), []);
  const onLandfillEmissionsCalculated = React.useCallback((emissions: number | null) => setLandfillEmissions2023(emissions), []);

  const chartEnabledLookup = React.useMemo(() => {
    const lookup: Record<string, boolean> = {};
    Object.values(overviewSettings || {}).forEach((sectionSettings) => {
      Object.entries(sectionSettings || {}).forEach(([chartId, enabled]) => {
        lookup[chartId] = !!enabled;
      });
    });
    return lookup;
  }, [overviewSettings]);

  const chartVisibilityLookup = React.useMemo(() => {
    const lookup: Record<string, string[] | null> = {};
    Object.values(overviewVisibilitySettings || {}).forEach((sectionSettings) => {
      Object.entries(sectionSettings || {}).forEach(([chartId, roles]) => {
        lookup[chartId] = Array.isArray(roles) ? roles : null;
      });
    });
    return lookup;
  }, [overviewVisibilitySettings]);

  const hasExplicitOverviewSettings = React.useMemo(
    () => Object.keys(overviewSettings || {}).length > 0,
    [overviewSettings]
  );

  const overviewDefaultByChartId = React.useMemo(() => {
    const defaults: Record<string, boolean> = {};
    OVERVIEW_SECTIONS.forEach((section) => {
      section.charts.forEach((chart) => {
        defaults[chart.id] = true;
      });
    });
    return defaults;
  }, []);

  const canCurrentUserViewChart = React.useCallback((sectionId: string, chartId: string) => {
    const sectionRoles = overviewVisibilitySettings?.[sectionId];
    const configuredRoles = sectionRoles && chartId in sectionRoles
      ? sectionRoles[chartId]
      : chartId in chartVisibilityLookup
        ? chartVisibilityLookup[chartId]
        : null;

    if (configuredRoles === null || configuredRoles === undefined) return true;
    if (configuredRoles.length === 0) return false;
    if (configuredRoles.includes('public')) return true;
    const roleOptions = configuredRoles.filter((role) =>
      ['super_admin', 'developer', 'academic', 'local_govt_admin', 'local_govt_admin_pending', 'user'].includes(role)
    ) as Array<'super_admin' | 'developer' | 'academic' | 'local_govt_admin' | 'local_govt_admin_pending' | 'user'>;
    return roleOptions.length > 0 ? hasAnyRole(roleOptions) : false;
  }, [overviewVisibilitySettings, chartVisibilityLookup, hasAnyRole]);

  const isOverviewChartEnabled = React.useCallback((sectionId: string, chartId: string) => {
    if (!overviewSettingsLoaded) return false;

    const sectionSettings = overviewSettings?.[sectionId];
    if (sectionSettings && chartId in sectionSettings) {
      return sectionSettings[chartId] !== false && canCurrentUserViewChart(sectionId, chartId);
    }
    // Fallback: match by chart_id across any section_id row so page-level includeOverview settings apply.
    if (chartId in chartEnabledLookup) {
      return chartEnabledLookup[chartId] !== false && canCurrentUserViewChart(sectionId, chartId);
    }
    // Keep new developer-only ranking table visible by default until explicitly configured.
    if (chartId === 'livability_rankings_2023' || chartId === 'livability_over_time') {
      return canCurrentUserViewChart(sectionId, chartId);
    }
    if (hasExplicitOverviewSettings) return false;
    if (chartId in overviewDefaultByChartId) {
      return overviewDefaultByChartId[chartId] !== false && canCurrentUserViewChart(sectionId, chartId);
    }
    return false;
  }, [overviewSettingsLoaded, overviewSettings, chartEnabledLookup, hasExplicitOverviewSettings, overviewDefaultByChartId, canCurrentUserViewChart]);

  const isOverviewSectionEnabled = React.useCallback((sectionId: string) => {
    if (!overviewSettingsLoaded) return false;
    const section = OVERVIEW_SECTIONS.find((item) => item.id === sectionId);
    if (!section) return false;
    return section.charts.some((chart) => isOverviewChartEnabled(sectionId, chart.id));
  }, [overviewSettingsLoaded, isOverviewChartEnabled]);

  const needsVehicleMiles = isOverviewChartEnabled('transportation', 'vehicle_miles');
  const needsSectorBasedGhg = isOverviewChartEnabled('ghg_inventory', 'sector_based_ghg');
  const needsConsumptionBasedGhg = isOverviewChartEnabled('ghg_inventory', 'consumption_based_ghg');
  const needsElectricity = isOverviewChartEnabled('buildings', 'electricity_consumption');
  const needsNaturalGas = isOverviewChartEnabled('buildings', 'natural_gas_consumption');
  const needsSolar = isOverviewChartEnabled('buildings', 'cumulative_solar_capacity');
  const needsBurdenPercentile = isOverviewChartEnabled('equity_index', 'burdens');
  const showLivabilityRankingsChart =
    hasAnyRole(['super_admin', 'developer']) &&
    isOverviewChartEnabled('equity_index', 'livability_rankings_2023');
  const showLivabilityOverTimeChart = isOverviewChartEnabled('equity_index', 'livability_over_time');
  const needsVmtPipeline = needsVehicleMiles || needsSectorBasedGhg;

  useEffect(() => {
    if (!showLivabilityOverTimeChart || !selectedLocation) {
      setLivabilityTrendRows([]);
      setLivabilityTrendLoading(false);
      setLivabilityTrendError(null);
      return;
    }

    const selectedLocationRecord = availableLocations.find((loc) => loc.name === selectedLocation);
    const locationType = selectedLocationRecord?.type === 'county' ? 'County' : 'City';
    const cacheKey = `${selectedLocation}|${locationType}`;
    const cachedRows = livabilityTrendCacheRef.current[cacheKey];
    if (cachedRows && cachedRows.length > 0) {
      setLivabilityTrendRows(cachedRows);
      setLivabilityTrendLoading(false);
      setLivabilityTrendError(null);
      return;
    }

    let isActive = true;
    const loadTrend = async () => {
      setLivabilityTrendLoading(true);
      setLivabilityTrendError(null);
      try {
        const { data, error } = await supabaseClient
          .from('livability_index_precalculated')
          .select(`
            year,
            total_score_percentile,
            total_burdens_percentile,
            total_benefits_percentile
          `)
          .eq('location_name', selectedLocation)
          .eq('geography_type', locationType)
          .order('year', { ascending: true })
          .range(0, 200);

        if (error) throw error;
        if (!isActive) return;

        const rowsRaw = (data || []) as Array<Record<string, any>>;
        const byYear = new Map<number, LivabilityTrendRow>();
        rowsRaw.forEach((row) => {
          const year = Number(row.year);
          if (!Number.isFinite(year)) return;
          const candidate: LivabilityTrendRow = {
            year,
            total_score_percentile: row.total_score_percentile ?? null,
            total_burdens_percentile: row.total_burdens_percentile ?? null,
            total_benefits_percentile: row.total_benefits_percentile ?? null
          };
          const existing = byYear.get(year);
          if (!existing) {
            byYear.set(year, candidate);
            return;
          }
          const existingCount = Object.values(existing).filter((v) => typeof v === 'number' && Number.isFinite(v)).length;
          const candidateCount = Object.values(candidate).filter((v) => typeof v === 'number' && Number.isFinite(v)).length;
          if (candidateCount > existingCount) byYear.set(year, candidate);
        });
        const cleaned = Array.from(byYear.values()).sort((a, b) => a.year - b.year);
        livabilityTrendCacheRef.current[cacheKey] = cleaned;
        setLivabilityTrendRows(cleaned);
      } catch (error) {
        console.error('Overview: Failed to load livability trend data:', error);
        if (!isActive) return;
        setLivabilityTrendRows([]);
        setLivabilityTrendError('Unable to load livability trend data right now.');
      } finally {
        if (isActive) setLivabilityTrendLoading(false);
      }
    };

    loadTrend();
    return () => {
      isActive = false;
    };
  }, [showLivabilityOverTimeChart, selectedLocation, availableLocations]);

  const livabilityTrendFigure = React.useMemo(() => {
    if (!livabilityTrendRows.length) return null;
    const years = livabilityTrendRows.map((row) => row.year);
    const toSeries = (field: keyof LivabilityTrendRow) =>
      livabilityTrendRows.map((row) => {
        const value = row[field];
        return typeof value === 'number' && Number.isFinite(value) ? value : null;
      });

    const totalsChartData = {
      labels: years,
      datasets: [
        {
          label: 'Livability Index',
          data: toSeries('total_score_percentile'),
          borderColor: '#1d4ed8',
          backgroundColor: 'transparent',
          borderWidth: 3,
          tension: 0.25
        },
        {
          label: 'Total Benefits',
          data: toSeries('total_benefits_percentile'),
          borderColor: '#059669',
          backgroundColor: 'transparent',
          borderWidth: 2,
          tension: 0.25
        },
        {
          label: 'Total Burdens',
          data: toSeries('total_burdens_percentile'),
          borderColor: '#dc2626',
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [6, 4],
          tension: 0.25
        }
      ]
    };

    const sharedOptions = {
      type: 'line',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { boxWidth: 12, padding: 12, usePointStyle: true }
        },
        tooltip: {
          callbacks: {
            label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed?.(1) ?? ctx.parsed.y}`
          }
        }
      },
      scales: {
        y: {
          min: 0,
          max: 100,
          title: { display: true, text: 'Percentile' }
        },
        x: {
          title: { display: true, text: 'Year' }
        }
      }
    };

    return {
      totalsChartData,
      totalsChartOptions: { ...sharedOptions }
    };
  }, [livabilityTrendRows]);

  useEffect(() => {
    if (!overviewSettingsLoaded || !showLivabilityRankingsChart) {
      setRankingTopRows([]);
      setRankingBottomRows([]);
      setRankingError(null);
      setRankingLoading(false);
      return;
    }

    let isActive = true;

    const calculateAndSetRankings = (rows: LivabilityRankingSourceRow[]) => {
      const yearCounts = new Map<number, number>();
      rows.forEach((row) => {
        const y = Number(row.year);
        if (!Number.isFinite(y)) return;
        yearCounts.set(y, (yearCounts.get(y) || 0) + 1);
      });
      // "Viable" years are those with at least one row containing non-null burdens + benefits.
      const availableYears = Array.from(yearCounts.keys()).sort((a, b) => b - a);
      setRankingAvailableYears(availableYears);
      if (availableYears.length === 0) {
        setRankingTopRows([]);
        setRankingBottomRows([]);
        setRankingError(`No viable ${rankingGeographyType.toLowerCase()} ranking data available.`);
        return;
      }

      let effectiveYear = rankingYear;
      if (!availableYears.includes(effectiveYear)) {
        effectiveYear = availableYears.includes(2023) ? 2023 : availableYears[0];
        setRankingYear(effectiveYear);
      }

      const dedupedByLocation = new Map<string, LivabilityRankingRow>();

      rows.forEach((row) => {
        if (Number(row.year) !== effectiveYear) return;
        const locationName = (row.location_name || '').trim();
        const geofips = (row.geofips || '').trim();
        const burdens = Number(row.total_burdens_percentile);
        const benefits = Number(row.total_benefits_percentile);

        if (!locationName || !geofips || !Number.isFinite(burdens) || !Number.isFinite(benefits)) return;

        // Match UI formula shown on the Overview Livability card.
        const score = (benefits + (100 - burdens)) / 2;
        const key = locationName.toLowerCase();
        const candidate: LivabilityRankingRow = {
          rank: 0,
          locationName,
          geofips,
          score,
          burdens,
          benefits
        };

        const existing = dedupedByLocation.get(key);
        if (!existing || candidate.score > existing.score) {
          dedupedByLocation.set(key, candidate);
        }
      });

      const sorted = Array.from(dedupedByLocation.values()).sort((a, b) => b.score - a.score);
      const rowLimit = rankingRowCount === 'all' ? sorted.length : rankingRowCount;
      const top = sorted.slice(0, rowLimit).map((row, idx) => ({ ...row, rank: idx + 1 }));
      const bottom = sorted
        .slice(Math.max(0, sorted.length - rowLimit))
        .reverse()
        .map((row, idx) => ({ ...row, rank: idx + 1 }));

      setRankingTopRows(top);
      setRankingBottomRows(bottom);
      setRankingError(null);
    };

    const loadLivabilityRankings = async () => {
      const cachedRows = rankingDataCacheRef.current[rankingGeographyType];
      if (cachedRows && cachedRows.length > 0) {
        calculateAndSetRankings(cachedRows);
        setRankingLoading(false);
        return;
      }

      setRankingLoading(true);
      setRankingError(null);

      try {
        const { data, error } = await supabaseClient
          .from('livability_index_precalculated')
          .select('location_name, geofips, geography_type, year, total_burdens_percentile, total_benefits_percentile')
          .eq('geography_type', rankingGeographyType)
          .not('total_burdens_percentile', 'is', null)
          .not('total_benefits_percentile', 'is', null)
          .range(0, 5000);

        if (error) throw error;
        if (!isActive) return;

        const rows = (data || []) as LivabilityRankingSourceRow[];
        rankingDataCacheRef.current[rankingGeographyType] = rows;
        calculateAndSetRankings(rows);
      } catch (error) {
        console.error('Overview: Failed to load livability rankings:', error);
        if (!isActive) return;
        setRankingTopRows([]);
        setRankingBottomRows([]);
        setRankingError('Unable to load rankings right now.');
      } finally {
        if (isActive) setRankingLoading(false);
      }
    };

    loadLivabilityRankings();

    return () => {
      isActive = false;
    };
  }, [
    overviewSettingsLoaded,
    showLivabilityRankingsChart,
    rankingGeographyType,
    rankingYear,
    rankingRowCount
  ]);

  const onVmtDataCalculated = React.useCallback(async (data: { year: number; vmt?: number }[] | null) => {
    if (!data || data.length === 0) {
      console.log('⚠️ Overview: VMT Chart onDataCalculated called with empty data, skipping');
      return;
    }
    const location = selectedLocation;
    if (vmtChartDataLoadedRef.current && ghgInventoryLoadedForLocationRef.current === location) {
      console.log('⏸️ Overview: VMT Chart data already processed for', location, '- skipping duplicate onDataCalculated call');
      return;
    }
    if (ghgInventoryLoadedForLocationRef.current && ghgInventoryLoadedForLocationRef.current !== location) {
      console.log('🔄 Overview: Location changed from', ghgInventoryLoadedForLocationRef.current, 'to', location, '- resetting refs');
      vmtChartDataLoadedRef.current = false;
      ghgInventoryLoadedForLocationRef.current = null;
      ghgInventoryLoadingRef.current = false;
    }
    console.log('✅ Overview: VMT Chart on Overview page has loaded data:', data?.length || 0, 'records');
    const { setVMTDataFromTransportation } = await import('../../services/vmtDataService');
    setVMTDataFromTransportation(data.map((d: any) => ({
      year: d.year,
      vmt: d.vmt || 0,
      source: 'overview_vmt_chart',
      method_note: 'VMT data from Overview page VMT chart - includes backcasted years 2005-2014'
    })));
    if (typeof window !== 'undefined') {
      (window as any).vmtChartData = data.map((d: any) => ({ year: d.year, vmt: d.vmt || 0 }));
    }
    if (!vmtChartDataLoadedRef.current) {
      setOverviewVMTChartLoaded(true);
      setVmtDataAvailable(true);
      setVmtDataReady(true);
      vmtChartDataLoadedRef.current = true;
    }
    if (needsSectorBasedGhg && location && !ghgInventoryLoadingRef.current && ghgInventoryLoadedForLocationRef.current !== location) {
      ghgInventoryLoadingRef.current = true;
      console.log('🔄 Overview: Loading GHG Inventory now that VMT chart has loaded for', location);
      fetchGHGInventoryData(location)
        .then((ghgResult) => {
          setGhgInventoryData(ghgResult);
          ghgInventoryLoadedForLocationRef.current = location;
          ghgInventoryLoadingRef.current = false;
        })
        .catch((error) => {
          console.warn('Overview: Sector-based GHG Inventory load failed:', error);
          setGhgInventoryData(null);
          ghgInventoryLoadingRef.current = false;
        });
    }
  }, [selectedLocation, needsSectorBasedGhg]);

  const renderSampleTitle = (title: React.ReactNode, includeSample = true) => (
    <span>
      {includeSample && <span style={{ fontWeight: 400 }}>Sample of </span>}
      <span style={{ fontWeight: 700 }}>{title}</span>
      {includeSample && <span style={{ fontWeight: 400 }}> Figures.</span>}
    </span>
  );

  const builtInOverviewChartIds = React.useMemo(() => {
    const ids = new Set<string>();
    OVERVIEW_SECTIONS.forEach((section) => {
      section.charts.forEach((chart) => ids.add(chart.id));
    });
    return ids;
  }, []);

  const additionalOverviewCharts = React.useMemo(() => {
    if (!overviewSettingsLoaded) return [];

    const selected: { id: string; label: string; pagePath: string }[] = [];
    ADMIN_PAGES.forEach((page) => {
      page.sections.forEach((section) => {
        section.charts.forEach((chart) => {
          if (builtInOverviewChartIds.has(chart.id)) {
            return;
          }
          if (isOverviewChartEnabled(section.id, chart.id)) {
            selected.push({
              id: chart.id,
              label: chart.label,
              pagePath: page.path
            });
          }
        });
      });
    });
    return selected;
  }, [overviewSettingsLoaded, builtInOverviewChartIds, isOverviewChartEnabled]);

  useEffect(() => {
    if (isLandingPage || !selectedLocation) {
      setOverviewSettings(null);
      setOverviewVisibilitySettings(null);
      setOverviewSettingsLoaded(false);
      return;
    }

    let isActive = true;
    setOverviewSettingsLoaded(false);

    const loadOverviewSettings = async () => {
      const fetchOverviewRows = async (locationName: string) => {
        const withRoles = await supabaseClient
          .from('admin_overview_settings')
          .select('section_id, chart_id, is_enabled, visible_roles')
          .eq('location_name', locationName);
        if (!withRoles.error) return withRoles;
        if (!/visible_roles/i.test(withRoles.error.message || '')) return withRoles;
        const withoutRoles = await supabaseClient
          .from('admin_overview_settings')
          .select('section_id, chart_id, is_enabled')
          .eq('location_name', locationName);
        if (withoutRoles.error) return withoutRoles as any;
        return {
          data: (withoutRoles.data || []).map((row: any) => ({ ...row, visible_roles: null })),
          error: null
        } as any;
      };

      const [localRes, globalRes] = await Promise.all([
        fetchOverviewRows(selectedLocation),
        fetchOverviewRows(GLOBAL_ADMIN_LOCATION)
      ]);

      if (localRes.error || globalRes.error) {
        console.error('Overview: Failed to load admin settings:', localRes.error || globalRes.error);
        if (!isActive) return;
        setOverviewSettings({});
        setOverviewSettingsLoaded(true);
        return;
      }

      const nextSettings: Record<string, Record<string, boolean>> = {};
      const nextVisibilitySettings: Record<string, Record<string, string[] | null>> = {};
      [...(globalRes.data || []), ...(localRes.data || [])].forEach((row: any) => {
        if (!nextSettings[row.section_id]) {
          nextSettings[row.section_id] = {};
        }
        if (!nextVisibilitySettings[row.section_id]) {
          nextVisibilitySettings[row.section_id] = {};
        }
        nextSettings[row.section_id][row.chart_id] = row.is_enabled;
        nextVisibilitySettings[row.section_id][row.chart_id] =
          Array.isArray(row.visible_roles) && row.visible_roles.length > 0 ? row.visible_roles : null;
      });

      if (!isActive) return;
      setOverviewSettings(nextSettings);
      setOverviewVisibilitySettings(nextVisibilitySettings);
      setOverviewSettingsLoaded(true);
    };

    loadOverviewSettings();

    return () => {
      isActive = false;
    };
  }, [isLandingPage, selectedLocation]);

  // Note: GHG Inventory loading is now handled by the Overview VMT chart's onDataCalculated callback
  // This ensures we wait for the VMT chart to finish loading before loading GHG Inventory

  // Track when Overview page is fully loaded (VMT + GHG Inventory) and set global flag
  useEffect(() => {
    if (isLandingPage || !selectedLocation || !overviewSettingsLoaded) {
      // Reset flag when on landing page or no location selected
      setOverviewFullyLoaded(false);
      if (typeof window !== 'undefined') {
        window.overviewFullyLoaded = false;
      }
      return;
    }

    // Overview is fully loaded when VMT chart has loaded AND GHG inventory has loaded
    // Check both the state flag and the ref, and also check if ghgInventoryData exists
    const isFullyLoaded = needsSectorBasedGhg
      ? (overviewVMTChartLoaded &&
          ghgInventoryLoadedForLocationRef.current === selectedLocation &&
          ghgInventoryData !== null)
      : (needsVmtPipeline ? overviewVMTChartLoaded : true);
    setOverviewFullyLoaded(isFullyLoaded);
    
    // Set global flag for Navigation component to check
    if (typeof window !== 'undefined') {
      window.overviewFullyLoaded = isFullyLoaded;
    }
    
    if (isFullyLoaded) {
      console.log('✅ Overview: Page fully loaded - VMT and GHG Inventory ready');
      console.log('✅ Overview: overviewVMTChartLoaded:', overviewVMTChartLoaded);
      console.log('✅ Overview: ghgInventoryLoadedForLocationRef:', ghgInventoryLoadedForLocationRef.current);
      console.log('✅ Overview: ghgInventoryData exists:', ghgInventoryData !== null);
    } else {
      console.log('⏳ Overview: Still loading - VMT:', overviewVMTChartLoaded, 'GHG:', ghgInventoryLoadedForLocationRef.current === selectedLocation, 'GHG Data:', ghgInventoryData !== null);
    }
  }, [overviewVMTChartLoaded, selectedLocation, isLandingPage, ghgInventoryData, needsSectorBasedGhg, needsVmtPipeline, overviewSettingsLoaded]);

  // Track when Overview load started (for "time left" indicator)
  useEffect(() => {
    if (selectedLocation && !isLandingPage && overviewSettingsLoaded && !overviewFullyLoaded) {
      if (overviewLoadStartRef.current === null) {
        overviewLoadStartRef.current = Date.now();
      }
    } else {
      overviewLoadStartRef.current = null;
      setOverviewLoadElapsedSec(0);
    }
  }, [selectedLocation, isLandingPage, overviewFullyLoaded, overviewSettingsLoaded]);

  // Update elapsed seconds every second while loading
  useEffect(() => {
    if (!selectedLocation || isLandingPage || !overviewSettingsLoaded || overviewFullyLoaded) return;
    const interval = setInterval(() => {
      const start = overviewLoadStartRef.current;
      if (start == null) return;
      const elapsed = Math.floor((Date.now() - start) / 1000);
      setOverviewLoadElapsedSec(elapsed);
    }, 1000);
    return () => clearInterval(interval);
  }, [selectedLocation, isLandingPage, overviewFullyLoaded, overviewSettingsLoaded]);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        await supabaseLocationService.initialize();
        const locations = supabaseLocationService.getAvailableLocations();
        setAvailableLocations(locations);
      } catch (error) {
        console.error('Failed to load locations:', error);
      } finally {
        setLoading(false);
      }
    };

    loadLocations();
  }, []);

  // Handle scroll detection for sticky location selector
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Preload data in background when location is selected on Overview dashboard
  useEffect(() => {
    if (!overviewSettingsLoaded) return;

    if (isLandingPage || !selectedLocation || !selectedGeofips) {
        // Clear data when location is not available
        setGhgInventoryData(null);
        setVmtDataReady(false);
        setVmtDataAvailable(false);
        setOverviewVMTChartLoaded(false);
        setOverviewFullyLoaded(false);
        ghgInventoryLoadingRef.current = false; // Reset loading flag
        ghgInventoryLoadedForLocationRef.current = null; // Reset location tracking
        vmtChartDataLoadedRef.current = false; // Reset VMT chart loaded flag
        if (typeof window !== 'undefined') {
          window.overviewFullyLoaded = false;
        }
      return;
    }

    console.log('🔄 Overview: Starting background data preload for', selectedLocation);

    // Preload data in sequence: Load VMT first (from Transportation page if available, otherwise load it here), then GHG inventory, then other charts
    const preloadData = async () => {
      try {
        // Clear GHG Inventory data and VMT flags when starting preload for new location
        setGhgInventoryData(null);
        setVmtDataReady(false);
        setVmtDataAvailable(false);
        setOverviewVMTChartLoaded(false);
        setOverviewFullyLoaded(false);
        ghgInventoryLoadingRef.current = false; // Reset loading flag
        ghgInventoryLoadedForLocationRef.current = null; // Reset location tracking
        vmtChartDataLoadedRef.current = false; // Reset VMT chart loaded flag
        if (typeof window !== 'undefined') {
          window.overviewFullyLoaded = false;
        }
        
        // 1. Load VMT data FIRST when needed (transportation VMT card and/or sector-based GHG)
        // Priority: Use VMT from Transportation page if available, otherwise load it here
        if (needsVmtPipeline) {
          console.log('🔄 Overview: Checking for VMT data...');
          const { isVMTDataAvailable, setVMTDataFromTransportation } = await import('../../services/vmtDataService');
        
        // Check if VMT data is already available (from Transportation page)
        let vmtAvailable = isVMTDataAvailable();
        setVmtDataAvailable(vmtAvailable);
        
          if (!vmtAvailable) {
          // Wait for VMT data from Transportation page (poll every 500ms, max 15 seconds)
          // This gives Transportation page time to load VMT in the background
          console.log('⏳ Overview: VMT data not yet available, waiting for Transportation page to load VMT...');
          let attempts = 0;
          const maxAttempts = 30; // 15 seconds max wait
          await new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
              attempts++;
              vmtAvailable = isVMTDataAvailable();
              setVmtDataAvailable(vmtAvailable);
              if (vmtAvailable) {
                console.log('✅ Overview: VMT data now available from Transportation page');
                clearInterval(checkInterval);
                setVmtDataReady(true);
                resolve();
              } else if (attempts >= maxAttempts) {
                console.log('⚠️ Overview: VMT data not available from Transportation page after waiting, loading VMT data here...');
                clearInterval(checkInterval);
                resolve();
              }
            }, 500);
          });
          } else {
            console.log('✅ Overview: VMT data already available from Transportation page');
            setVmtDataReady(true);
          }

        // If VMT still not available, load it here (same method as Transportation page)
          if (!vmtAvailable) {
          console.log('🔄 Overview: Loading VMT data (Transportation page not visited yet)...');
          try {
            // Fetch all three data sources like VMTChart does
            const [rawVmtDataResult, backcastedVmtDataResult, emfacDataResult] = await Promise.all([
              DataService.getVMTData(selectedLocation, undefined, selectedGeofips).catch(err => {
                console.warn('VMT raw data fetch failed:', err);
                return [];
              }),
              DataService.getVMTDataWithScaling(selectedGeofips, 'Combined', '2025', '2025').catch(err => {
                console.warn('VMT backcasted data fetch failed:', err);
                return [];
              }),
              DataService.getEMFACBackcastingDataByGeofips(selectedGeofips, [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014], 'Combined').catch(err => {
                console.warn('EMFAC data not available, continuing without it:', err);
                return [];
              })
            ]);
            
            // Filter to only include PRIVATE_AUTO and ON_DEMAND modes (like VMTChart)
            const rawPrivateAuto = rawVmtDataResult.filter((d: any) => d.mode === 'PRIVATE_AUTO');
            const rawOnDemand = rawVmtDataResult.filter((d: any) => d.mode === 'ON_DEMAND_AUTO');
            const backcastedPrivateAuto = backcastedVmtDataResult.filter((d: any) => d.mode === 'PRIVATE_AUTO');
            const backcastedOnDemand = backcastedVmtDataResult.filter((d: any) => d.mode === 'ON_DEMAND_AUTO');
            
            // Get 2015 VMT data as the baseline for EMFAC backcasting
            const vmt2015PrivateAuto = backcastedPrivateAuto.find((d: any) => d.year === 2015);
            const vmt2015OnDemand = backcastedOnDemand.find((d: any) => d.year === 2015);
            const vmt2015Total = (vmt2015PrivateAuto?.vmt || 0) + (vmt2015OnDemand?.vmt || 0);
            
            // Process EMFAC backcasting for 2005-2014 (same logic as VMTChart)
            const emfacBackcastedData: any[] = [];
            const emfacYears = [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014];
            
            if (vmt2015Total > 0 && emfacDataResult.length > 0) {
              // Sort ratios by year descending (2014, 2013, ..., 2005)
              const sortedRatios = emfacDataResult
                .filter((r: any) => emfacYears.includes(r.year))
                .sort((a: any, b: any) => b.year - a.year);
              
              // Calculate VMT for each year sequentially
              const vmtByYear = new Map<number, number>();
              vmtByYear.set(2015, vmt2015Total);
              
              // Apply ratios sequentially: year Y = year Y+1 * ratio_Y
              for (const emfacRecord of sortedRatios) {
                const nextYear = emfacRecord.year + 1;
                const nextYearVMT = vmtByYear.get(nextYear);
                
                if (nextYearVMT && emfacRecord.ratio) {
                  const currentYearVMT = nextYearVMT * emfacRecord.ratio;
                  vmtByYear.set(emfacRecord.year, currentYearVMT);
                }
              }
              
              // Create data points for each year, distributing between PRIVATE_AUTO and ON_DEMAND
              const privateAutoRatio = vmt2015PrivateAuto ? vmt2015PrivateAuto.vmt / vmt2015Total : 0.8;
              const onDemandRatio = 1 - privateAutoRatio;
              
              for (const year of emfacYears) {
                const yearVMT = vmtByYear.get(year);
                if (yearVMT !== undefined && yearVMT > 0) {
                  emfacBackcastedData.push({
                    year: year,
                    mode: 'PRIVATE_AUTO',
                    vmt: yearVMT * privateAutoRatio
                  });
                  emfacBackcastedData.push({
                    year: year,
                    mode: 'ON_DEMAND_AUTO',
                    vmt: yearVMT * onDemandRatio
                  });
                }
              }
            }
            
            // Combine all years: EMFAC (2005-2014) + AADT backcasted (2015-2022) + Raw (2023)
            const allYears = Array.from(new Set([
              ...emfacBackcastedData.map((d: any) => d.year),
              ...backcastedPrivateAuto.map((d: any) => d.year),
              ...backcastedOnDemand.map((d: any) => d.year),
              ...rawPrivateAuto.map((d: any) => d.year),
              ...rawOnDemand.map((d: any) => d.year)
            ])).sort();
            
            // Create combined VMT data for all years
            const combinedVmtData = allYears.map(year => {
              if (year === 2023) {
                // Use Replica data for 2023
                const privateAutoVmt = rawPrivateAuto.find((d: any) => d.year === year)?.vmt || 0;
                const onDemandVmt = rawOnDemand.find((d: any) => d.year === year)?.vmt || 0;
                return { year, vmt: privateAutoVmt + onDemandVmt };
              } else if (year >= 2005 && year <= 2014) {
                // Use EMFAC backcasted data for 2005-2014
                const privateAutoVmt = emfacBackcastedData.find((d: any) => d.year === year && d.mode === 'PRIVATE_AUTO')?.vmt || 0;
                const onDemandVmt = emfacBackcastedData.find((d: any) => d.year === year && d.mode === 'ON_DEMAND_AUTO')?.vmt || 0;
                return { year, vmt: privateAutoVmt + onDemandVmt };
              } else {
                // Use AADT backcasted data for 2015-2022
                const privateAutoVmt = backcastedPrivateAuto.find((d: any) => d.year === year)?.vmt || 0;
                const onDemandVmt = backcastedOnDemand.find((d: any) => d.year === year)?.vmt || 0;
                return { year, vmt: privateAutoVmt + onDemandVmt };
              }
            });
            
            if (combinedVmtData.length > 0) {
              const vmtData = combinedVmtData.map((d: any) => ({
                year: d.year,
                vmt: d.vmt || 0,
                source: 'overview_preload',
                method_note: 'Preloaded from Overview page (Transportation page not visited)'
              }));
              
              // Set VMT data in the service FIRST
              setVMTDataFromTransportation(vmtData);
              
              // Also set in window variable (legacy, but GHG Inventory checks this first)
              if (typeof window !== 'undefined') {
                window.vmtChartData = vmtData.map((d: any) => ({
                  year: d.year,
                  vmt: d.vmt || 0
                }));
              }
              
              // Verify VMT data is now available before proceeding
              const { isVMTDataAvailable: checkVMT } = await import('../../services/vmtDataService');
              const vmtNowAvailable = checkVMT();
              
              if (vmtNowAvailable) {
                console.log('✅ Overview: VMT data loaded and verified in service:', vmtData.length, 'records');
              console.log('✅ Overview: VMT years included:', vmtData.map((d: any) => d.year).sort());
              vmtAvailable = true;
                setVmtDataAvailable(true);
              setVmtDataReady(true);
              } else {
                console.error('❌ Overview: VMT data was set but isVMTDataAvailable() returned false');
                vmtAvailable = false;
                setVmtDataAvailable(false);
                setVmtDataReady(false);
              }
            } else if (rawVmtDataResult.length > 0) {
              // Fallback to raw data if backcasted data is not available
              const vmtData = rawVmtDataResult
                .filter((d: any) => d.mode === 'PRIVATE_AUTO' || d.mode === 'ON_DEMAND_AUTO')
                .reduce((acc: any, d: any) => {
                  const existing = acc.find((item: any) => item.year === d.year);
                  if (existing) {
                    existing.vmt += (d.vmt || 0);
                  } else {
                    acc.push({ year: d.year, vmt: d.vmt || 0 });
                  }
                  return acc;
                }, [])
                .map((d: any) => ({
                  year: d.year,
                  vmt: d.vmt || 0,
                  source: 'overview_preload_raw',
                  method_note: 'Preloaded from Overview page (raw data, Transportation page not visited)'
                }));
              
              setVMTDataFromTransportation(vmtData);
              
              if (typeof window !== 'undefined') {
                window.vmtChartData = vmtData.map((d: any) => ({
                  year: d.year,
                  vmt: d.vmt || 0
                }));
              }
              
              // Verify VMT data is now available before proceeding
              const { isVMTDataAvailable: checkVMT } = await import('../../services/vmtDataService');
              const vmtNowAvailable = checkVMT();
              
              if (vmtNowAvailable) {
                console.log('✅ Overview: VMT data loaded (raw) and verified:', vmtData.length, 'records');
              vmtAvailable = true;
                setVmtDataAvailable(true);
              setVmtDataReady(true);
              } else {
                console.error('❌ Overview: VMT data was set but isVMTDataAvailable() returned false');
                vmtAvailable = false;
                setVmtDataAvailable(false);
                setVmtDataReady(false);
              }
            }
          } catch (error) {
            console.warn('Overview: VMT load failed:', error);
          }
          } else {
            // VMT was already available from Transportation page
            setVmtDataAvailable(true);
            setVmtDataReady(true);
          }
        } else {
          // No VMT-dependent cards selected on Overview; skip VMT preload entirely.
          setVmtDataAvailable(true);
          setVmtDataReady(true);
          setOverviewVMTChartLoaded(true);
          vmtChartDataLoadedRef.current = true;
        }

        // 2. Wait for Overview page VMT chart to load before loading GHG Inventory
        // The VMT chart will load data from Transportation page if available
        // We need to wait for the chart to finish loading before proceeding
        console.log('⏳ Overview: Waiting for Overview page VMT chart to load...');
        
        // Don't preload GHG Inventory here - wait for VMT chart onDataCalculated callback

        // 3-7. Load all independent data in parallel (don't depend on VMT or each other)
        console.log('🔄 Overview: Loading independent data in parallel...');
        const preloadTasks: Promise<void>[] = [];
        // 3. Consumption-based GHG data (independent of VMT)
        if (needsConsumptionBasedGhg) {
          preloadTasks.push(fetchConsumptionBasedGHGData(selectedLocation)
            .then((consumptionResult) => {
          setConsumptionGHGData(consumptionResult);
          console.log('✅ Overview: Consumption-based GHG data preloaded');
            })
            .catch((error) => {
          console.warn('Overview: Consumption-based GHG preload failed:', error);
            }));
        }

          // 4. Housing Burden percentile (independent of VMT)
        if (needsBurdenPercentile) preloadTasks.push((async () => {
        try {
          // Get latest year's renter percentile (35%+ burden)
          const renterResult = await supabaseClient
            .from('housing_burden_renter')
            .select('year, percentile')
            .ilike('location_name', `%${selectedLocation}%`)
            .eq('housing_burden', '35.0 percent or more')
            .not('percentile', 'is', null)
            .gt('percentile', 0)
            .order('year', { ascending: false })
            .limit(1);
          
          if (renterResult.data && renterResult.data.length > 0 && renterResult.data[0].percentile) {
            const percentile = renterResult.data[0].percentile;
            const percentile100 = percentile <= 1 ? percentile * 100 : percentile;
            setHousingBurdenPercentile(percentile100);
            console.log('✅ Overview: Housing Burden percentile loaded:', percentile100);
          } else {
            // Try owner data as fallback
            const ownerResult = await supabaseClient
              .from('owner_housing_burden')
              .select('year, percentile')
              .ilike('location_name', `%${selectedLocation}%`)
              .eq('owner_housing_burden_level', '35.0 percent or more')
              .eq('mortgage_status', 'All')
              .not('percentile', 'is', null)
              .gt('percentile', 0)
              .order('year', { ascending: false })
              .limit(1);
            
            if (ownerResult.data && ownerResult.data.length > 0 && ownerResult.data[0].percentile) {
              const percentile = ownerResult.data[0].percentile;
              const percentile100 = percentile <= 1 ? percentile * 100 : percentile;
              setHousingBurdenPercentile(percentile100);
              console.log('✅ Overview: Housing Burden percentile loaded from owner data:', percentile100);
            }
          }
        } catch (error) {
          console.warn('Overview: Housing Burden percentile preload failed:', error);
        }
          })());

          // 5-6. Electricity and Natural Gas data (load once, use for both)
        if (needsElectricity || needsNaturalGas) preloadTasks.push((async () => {
            try {
              const [elecRes, elecCom, gasRes, gasCom] = await Promise.all([
                needsElectricity ? fetchElectricitySeries(selectedLocation, 'residential').catch(() => null) : Promise.resolve(null),
                needsElectricity ? fetchElectricitySeries(selectedLocation, 'commercial').catch(() => null) : Promise.resolve(null),
                needsNaturalGas ? fetchNaturalGasSeries(selectedLocation, 'residential').catch(() => null) : Promise.resolve(null),
                needsNaturalGas ? fetchNaturalGasSeries(selectedLocation, 'commercial').catch(() => null) : Promise.resolve(null)
              ]);
              
              // Process Electricity data
              if (elecRes && elecCom) {
                const elecStackedData = calculateStackedData(elecRes, elecCom, 'Electricity');
                if (elecStackedData) {
                  setElecStacked(elecStackedData);
                  // Calculate percent change since 2005
                  const labels = elecStackedData.chartData.labels as number[];
                  const datasets = elecStackedData.chartData.datasets;
                  if (labels.length > 0 && datasets.length > 0) {
                    const year2005Index = labels.indexOf(2005);
                    const latestYearIndex = labels.length - 1;
                    if (year2005Index >= 0 && latestYearIndex >= 0) {
                      const total2005 = datasets.reduce((sum: number, ds: any) => sum + (ds.data[year2005Index] || 0), 0);
                      const totalLatest = datasets.reduce((sum: number, ds: any) => sum + (ds.data[latestYearIndex] || 0), 0);
                      if (total2005 > 0) {
                        const changePercent = ((totalLatest - total2005) / total2005) * 100;
                        setElecChangeSince2005(changePercent);
                      } else {
                        setElecChangeSince2005(null);
                      }
                    }
                  }
                }
              }
              
              // Process Natural Gas data
          if (gasRes && gasCom) {
            const gasStackedData = calculateStackedData(gasRes, gasCom, 'Natural Gas');
            if (gasStackedData) {
              setGasStacked(gasStackedData);
                  // Calculate percent change since 2005
                  const labels = gasStackedData.chartData.labels as number[];
                  const datasets = gasStackedData.chartData.datasets;
                  if (labels.length > 0 && datasets.length > 0) {
                    const year2005Index = labels.indexOf(2005);
                    const latestYearIndex = labels.length - 1;
                    if (year2005Index >= 0 && latestYearIndex >= 0) {
                      const total2005 = datasets.reduce((sum: number, ds: any) => sum + (ds.data[year2005Index] || 0), 0);
                      const totalLatest = datasets.reduce((sum: number, ds: any) => sum + (ds.data[latestYearIndex] || 0), 0);
                      if (total2005 > 0) {
                        const changePercent = ((totalLatest - total2005) / total2005) * 100;
                        setGasChangeSince2005(changePercent);
                      } else {
                        setGasChangeSince2005(null);
                      }
                    }
                  }
                }
              }
              
              console.log('✅ Overview: Electricity and Natural Gas data preloaded');
        } catch (error) {
              console.warn('Overview: Electricity/Natural Gas preload failed:', error);
            }
          })());

          // 7. Solar PV data - calculate cumulative capacity (independent of VMT)
        if (needsSolar) preloadTasks.push((async () => {
            try {
              if (selectedGeofips) {
                // Fetch solar data to calculate cumulative capacity
                const { data: solarData } = await supabaseClient
                  .from('all_pv_size')
                  .select('year, sector, total_watts')
                  .eq('geoid', selectedGeofips)
                  .lte('year', 2024)
                  .order('year', { ascending: true });
                
                if (solarData && solarData.length > 0) {
                  // Calculate cumulative capacity by summing all installations across all years
                  // This matches how CumulativeSolarCapacityChart calculates it
                  const yearSectorMap = new Map<number, Map<string, number>>();
                  solarData.forEach((record: any) => {
                    const year = parseInt(record.year.toString());
                    if (year > 2024) return;
                    
                    if (!yearSectorMap.has(year)) {
                      yearSectorMap.set(year, new Map());
                    }
                    const sectorMap = yearSectorMap.get(year)!;
                    const sector = record.sector || 'unknown';
                    const capacity = (parseFloat(record.total_watts.toString()) || 0) / 1000; // Convert to kW
                    sectorMap.set(sector, (sectorMap.get(sector) || 0) + capacity);
                  });
                  
                  // Calculate cumulative capacity (sum all sectors across all years)
                  const allSectors = new Set<string>();
                  yearSectorMap.forEach(sectorMap => {
                    sectorMap.forEach((_, sector) => allSectors.add(sector));
                  });
                  
                  const sortedYears = Array.from(yearSectorMap.keys()).sort();
                  const sectorCumulativeMap = new Map<string, number>();
                  allSectors.forEach(sector => sectorCumulativeMap.set(sector, 0));
                  
                  sortedYears.forEach(year => {
                    const yearSectorMap_data = yearSectorMap.get(year) || new Map();
                    allSectors.forEach(sector => {
                      const sectorCapacity = yearSectorMap_data.get(sector) || 0;
                      const cumulativeSectorCapacity = sectorCumulativeMap.get(sector)! + sectorCapacity;
                      sectorCumulativeMap.set(sector, cumulativeSectorCapacity);
                    });
                  });
                  
                  const cumulativeCapacityKW = Array.from(sectorCumulativeMap.values()).reduce((sum, val) => sum + val, 0);
                  const cumulativeMW = cumulativeCapacityKW / 1000; // Convert kW to MW
                  setCumulativeSolarCapacity(cumulativeMW);
                }
              }
          console.log('✅ Overview: Solar PV data preloaded');
        } catch (error) {
          console.warn('Overview: Solar PV preload failed:', error);
        }
          })());
        await Promise.all(preloadTasks);

        console.log('✅ Overview: Background data preload completed');
      } catch (error) {
        console.error('Overview: Error during background preload:', error);
      }
    };

    // Start preloading in background (don't await)
    preloadData();
  }, [
    overviewSettingsLoaded,
    selectedLocation,
    selectedGeofips,
    isLandingPage,
    needsVmtPipeline,
    needsSectorBasedGhg,
    needsConsumptionBasedGhg,
    needsBurdenPercentile,
    needsElectricity,
    needsNaturalGas,
    needsSolar
  ]);

  const cities = availableLocations.filter(loc => loc.type === 'city');
  const counties = availableLocations.filter(loc => loc.type === 'county');

  // If this is the landing page, show landing page content
  if (isLandingPage) {
    return (
      <div className="overview-page">
        {/* Hero Slideshow */}
        <HeroSlideshow />
        
        {/* Page Header - New Tools Hub Title */}
        <PageHeader 
          title="Climate Action Tools & Resources"
          subtitle="Access comprehensive climate action planning tools, calculators, and data resources for California communities"
          variant="overview"
        />

      <div className="overview-content">
        {/* California Climate Action Tool Section - Gray Background */}
        <div className="tool-section tool-section-gray">
          <div className="section-container">
            <h2 className="tool-title">California Climate Action Planning Tool</h2>
            <div className="tool-content">
              {/* Left Column - Tool Description */}
              <div className="tool-description">
                <h3>Climate Action Data for California Communities</h3>
                <p>
                  Access comprehensive climate indicators, emissions data, and sustainability metrics 
                  for California cities and counties to support evidence-based climate action planning.
                </p>
                
                <div className="tool-features">
                  <h4>Data Categories:</h4>
                  <ul>
                    <li><strong>Emissions:</strong> Sector-Based and Consumption-Based GHG Inventories</li>
                    <li><strong>Transportation:</strong> VMT trends, EV adoption, and transit metrics</li>
                    <li><strong>Energy:</strong> Energy consumption, built environment, solar photovoltaics</li>
                    <li><strong>Demographics:</strong> Population, health, and equity indicators</li>
                    <li><strong>Land Use:</strong> Waste management and environmental data</li>
                  </ul>
                </div>
              </div>

              {/* Right Column - Location Selector & Stats */}
              <div className="tool-action">
                <div className={`location-selector-wrapper ${isScrolled ? 'scrolled' : ''}`}>
                  <h4>Explore a Location</h4>
                  <LocationSelector 
                    onLocationSelect={onLocationSelect}
                    placeholder="Search for any California city or county"
                    className="main-location-selector"
                  />
                  
                  {/* Data Coverage Stats */}
                {!loading && (
                  <div className="locations-summary">
                    <h4>Data Coverage</h4>
                    <div className="summary-stats">
                      <div className="stat-item">
                        <span className="stat-number">{cities.length}</span>
                        <span className="stat-label">Cities</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">{counties.length}</span>
                        <span className="stat-label">Counties</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">{availableLocations.length}</span>
                        <span className="stat-label">Total</span>
                      </div>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CoolClimate Calculator Section - White Background */}
        <div className="tool-section tool-section-white">
          <div className="section-container">
            <h2 className="tool-title">CoolClimate Calculator</h2>
            <div className="tool-content">
              {/* Left Column - Tool Screenshot */}
              <div className="tool-screenshot">
                <div className="screenshot-placeholder">
                  <div className="placeholder-content">
                    <div className="placeholder-icon"></div>
                    <div className="placeholder-title">CoolClimate Calculator</div>
                    <div className="placeholder-subtitle">Carbon Footprint Tool</div>
                    <div className="placeholder-features">
                      <div className="feature-item">Home Energy</div>
                      <div className="feature-item">Transportation</div>
                      <div className="feature-item">Food & Shopping</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Tool Description & Action */}
              <div className="tool-description">
                <h3>Calculate Your Household Carbon Footprint</h3>
                <p>
                  Use the CoolClimate Calculator to assess your household's environmental impact across 
                  travel, home energy, food consumption, and shopping habits.
                </p>
                
                <div className="tool-features">
                  <h4>What You'll Get:</h4>
                  <ul>
                    <li><strong>Personalized Assessment:</strong> Calculate your household carbon footprint</li>
                    <li><strong>Benchmark Comparison:</strong> Compare your results to similar households</li>
                    <li><strong>Action Planning:</strong> Create a personalized climate action plan</li>
                    <li><strong>Category Breakdown:</strong> See emissions by travel, home, food, goods, and services</li>
                  </ul>
                </div>
                
                <div className="tool-cta">
                  <a href="/coolclimate" className="tool-go-button">
                    Go to Calculator →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* EV Equity Roadmap Section - Gray Background */}
        <div className="tool-section tool-section-gray">
          <div className="section-container">
            <h2 className="tool-title">EV Equity Roadmap</h2>
            <div className="tool-content">
              {/* Left Column - Tool Screenshot */}
              <div className="tool-screenshot">
                <img 
                  src="/images/ev_map.png" 
                  alt="EV Equity Roadmap Tool Screenshot" 
                  className="tool-image"
                />
              </div>

              {/* Right Column - Tool Description & Action */}
              <div className="tool-description">
                <h3>Identify High-Priority EV Charging Sites</h3>
                <p>
                  This tool is intended for free use by local governments and stakeholders to identify 
                  high-priority, high-feasibility sites for investment in electric vehicle charging and 
                  associated mobility infrastructure.
                </p>
                
                <div className="tool-features">
                  <h4>About This Tool:</h4>
                  <ul>
                    <li><strong>Joint Project:</strong> UC Berkeley's Energy and Resources Group and Center for Law, Energy & the Environment (CLEE)</li>
                    <li><strong>EV Equity Initiative:</strong> Part of CLEE's broader equity-focused program</li>
                    <li><strong>Public Data:</strong> All data acquired from public sources except as noted</li>
                    <li><strong>Funding:</strong> Initial funding provided by UC Berkeley Institute for Transportation Studies</li>
                  </ul>
                </div>
                
                <div className="tool-cta">
                  <a href="https://evmap.climateplans.org/" target="_blank" rel="noopener noreferrer" className="tool-go-button">
                    Go to EV Roadmap →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* State of Change Newsletter Section - Gray Background */}
        <div className="tool-section tool-section-gray">
          <div className="section-container">
            <h2 className="tool-title">State of Change Newsletter</h2>
            <div className="tool-content">
              {/* Left Column - Newsletter Description */}
              <div className="tool-description">
                <h3>Climate Equity Reporting from UC Berkeley</h3>
                <p>
                  Stay informed with the latest climate equity reporting from the UC Berkeley Climate Journalism Lab. 
                  Our monthly newsletter covers California's climate action, environmental justice, and the communities 
                  most affected by climate change.
                </p>
                
                <div className="tool-features">
                  <h4>Recent Coverage:</h4>
                  <ul>
                    <li><strong>Manufacturing & Energy:</strong> Electric coffee roasting, biomass energy, and low-carbon manufacturing</li>
                    <li><strong>Transportation:</strong> Electric school buses, getting people out of cars, and transit-friendly housing</li>
                    <li><strong>Climate Justice:</strong> Sea level rise equity, polluter accountability, and environmental justice</li>
                    <li><strong>Renewable Energy:</strong> Solar panels on farms and canals, renewable energy policy impacts</li>
                    <li><strong>Policy & Politics:</strong> CEQA updates, climate bond impacts, and federal policy changes</li>
                  </ul>
                </div>
                
                <div className="tool-cta">
                  <a href="https://climateequity.substack.com/" target="_blank" rel="noopener noreferrer" className="tool-go-button">
                    Subscribe to Newsletter →
                  </a>
                  <p style={{ marginTop: '16px', fontSize: '0.9rem', color: '#64748b' }}>
                    Free monthly newsletter from the Climate Equity Reporting Project
                  </p>
                </div>
              </div>

              {/* Right Column - Newsletter Preview */}
              <div className="tool-screenshot">
                <div className="newsletter-preview">
                  <div className="newsletter-header">
                    <div className="newsletter-branding">
                      <span className="newsletter-brand">UC Berkeley Journalism</span>
                      <div className="newsletter-divider"></div>
                    </div>
                    <h4 className="newsletter-title">STATE OF CHANGE</h4>
                    <p className="newsletter-subtitle">A NEWSLETTER FROM THE CLIMATE EQUITY REPORTING PROJECT</p>
                  </div>
                  
                  <div className="newsletter-content">
                    <div className="newsletter-article">
                      <h5>Electric Coffee Roasting</h5>
                      <p>Exploring the path to low-carbon manufacturing through electric coffee roasting and other industrial electrification solutions.</p>
                    </div>
                    
                    <div className="newsletter-article">
                      <h5>Sea Level Rise Equity</h5>
                      <p>How do we prepare for sea level rise with equity in mind? Plus updates on the Big Beautiful Bill's climate impact.</p>
                    </div>
                    
                    <div className="newsletter-article">
                      <h5>Solar on Farms & Canals</h5>
                      <p>Farmers navigating renewable energy investments and the first solar tunnel going up over California's canals.</p>
                    </div>
                  </div>
                  
                  <div className="newsletter-footer">
                    <span className="newsletter-tag">Climate Equity • UC Berkeley • Free Subscription</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Survey Section - White Background */}
        <div className="tool-section tool-section-white">
          <div className="section-container">
            <h2 className="tool-title">Help Us Improve These Tools</h2>
            <div className="tool-content">
              {/* Left Column - Survey Description */}
              <div className="tool-description">
                <h3>UCB CAP Tools Local Government Survey</h3>
                <p>
                  We're developing greenhouse gas (GHG) inventories and policy recommendations for local jurisdictions statewide. 
                  Your input will help us create tools that truly meet the needs of California communities.
                </p>
                
                <div className="tool-features">
                  <h4>What We're Looking For:</h4>
                  <ul>
                    <li><strong>Current Needs:</strong> What challenges does your jurisdiction face?</li>
                    <li><strong>Tool Gaps:</strong> What's missing from existing climate planning tools?</li>
                    <li><strong>User Experience:</strong> How can we make these tools more useful?</li>
                    <li><strong>Data Priorities:</strong> What information is most valuable for your work?</li>
                  </ul>
                </div>
                
                <div className="tool-cta">
                  <a href="https://www.surveymonkey.com/r/MN7CMLS" target="_blank" rel="noopener noreferrer" className="tool-go-button">
                    Take the Survey →
                  </a>
                  <p style={{ marginTop: '16px', fontSize: '0.9rem', color: '#64748b' }}>
                    Prefer a 30-60 minute interview? Email Miya Kitahara at miya@stopwaste.org
                  </p>
                </div>
              </div>

              {/* Right Column - QR Code */}
              <div className="tool-screenshot">
                <div className="survey-qr-container">
                  <img 
                    src="/images/survey_qr.png" 
                    alt="Survey QR Code" 
                    className="survey-qr-code"
                  />
                  <div className="qr-label">
                    <strong>Scan to take the survey</strong>
                    <br />
                    <span style={{ fontSize: '0.9rem', color: '#64748b' }}>
                      UCB CAP Tools Local Government Survey
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stay Updated Section - Gray Background */}
        <div className="tool-section tool-section-gray">
          <div className="section-container">
            <div className="signup-link-section" style={{ 
              maxWidth: '600px',
              margin: '0 auto',
              padding: '20px', 
              backgroundColor: '#f0f9ff', 
              border: '2px solid #0ea5e9', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <h4 style={{ marginTop: '0', marginBottom: '12px', color: '#0369a1', fontSize: '1.2rem' }}>
                📧 Stay Updated
              </h4>
              <p style={{ marginBottom: '16px', color: '#334155', fontSize: '0.95rem' }}>
                Sign up to receive updates and news about this website
              </p>
              
              <div style={{ 
                marginBottom: '16px', 
                textAlign: 'left',
                backgroundColor: 'white',
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1'
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start',
                  cursor: 'pointer',
                  gap: '8px'
                }}>
                  <input
                    type="checkbox"
                    checked={acceptCommunications}
                    onChange={(e) => setAcceptCommunications(e.target.checked)}
                    style={{
                      marginTop: '4px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                  <span style={{ 
                    fontSize: '0.9rem', 
                    color: '#334155',
                    lineHeight: '1.5'
                  }}>
                    I agree to receive communications from ClimatePlans. We will not share contact information with third parties.
                  </span>
                </label>
              </div>
              
              <a 
                href="https://forms.office.com/pages/responsepage.aspx?id=2HwTd62DSUaIGt-8OLdWaH-Lquxf1HpKjg7IJiaZCVNUMjNIT0hDU004RFdCNjFQUTdaUTNZSzRBNC4u&route=shorturl"
                target="_blank"
                rel="noopener noreferrer"
                className="tool-go-button"
                style={{
                  display: 'inline-block',
                  padding: '12px 24px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  backgroundColor: acceptCommunications ? '#0ea5e9' : '#94a3b8',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '6px',
                  transition: 'background-color 0.2s',
                  cursor: acceptCommunications ? 'pointer' : 'not-allowed',
                  pointerEvents: acceptCommunications ? 'auto' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (acceptCommunications) {
                    e.currentTarget.style.backgroundColor = '#0284c7';
                  }
                }}
                onMouseLeave={(e) => {
                  if (acceptCommunications) {
                    e.currentTarget.style.backgroundColor = '#0ea5e9';
                  }
                }}
              >
                Sign Up
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  }

  // Dashboard view - show when isLandingPage is false
  // If no location selected, show message to select location
  if (!selectedLocation) {
    return (
      <div className="overview-page">
        <PageHeader 
          title="Overview"
          subtitle="Please select a location to view the overview dashboard."
          variant="overview"
        />
        <div className="overview-content" style={{ padding: '40px 20px', textAlign: 'center' }}>
          <p style={{ fontSize: '1.1rem', color: '#64748b', marginBottom: '30px' }}>
            Select a location from the header to view climate action data and metrics.
          </p>
        </div>
      </div>
    );
  }

  // Dashboard with location selected
  const estimatedSecLeft = Math.max(0, TYPICAL_OVERVIEW_LOAD_SEC - overviewLoadElapsedSec);
  const showLoadIndicator = !overviewFullyLoaded && selectedLocation && !isLandingPage;

  return (
    <div className="overview-page">
      <PageHeader 
        title={`Overview - ${selectedLocation}`}
        subtitle="Sample of Climate Action data and figures"
        variant="overview"
      />

      {showLoadIndicator && (
        <div className="overview-load-indicator" role="status" aria-live="polite">
          <span className="overview-load-indicator__text">
            Loading Overview…
          </span>
          <span className="overview-load-indicator__elapsed">
            {overviewLoadElapsedSec}s elapsed
          </span>
          <span className="overview-load-indicator__remaining">
            {overviewLoadElapsedSec >= TYPICAL_OVERVIEW_LOAD_SEC
              ? 'Almost ready…'
              : `About ${estimatedSecLeft}–${Math.max(1, estimatedSecLeft + 8)} sec left`}
          </span>
        </div>
      )}
      
      <div className="overview-content" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        {/* Section 1: Burden/Benefits Charts */}
        {isOverviewSectionEnabled('equity_index') && (
          <div className="tool-section tool-section-gray">
            <div className="section-container">
              <h2 style={{ fontSize: '1.8rem', fontWeight: '400', marginBottom: '20px', textAlign: 'center' }}>
                {renderSampleTitle(
                  `Livability & Climate Equity Index${burdenPercentile !== null && benefitsPercentile !== null ? `: ${((benefitsPercentile / 100 + (1 - burdenPercentile / 100)) / 2 * 100).toFixed(1)}` : ''}`,
                  false
                )}
              </h2>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '20px',
                marginBottom: '20px'
              }}>
                {isOverviewChartEnabled('equity_index', 'burdens') && (
                  <div style={{ border: '2px solid #dc2626', borderRadius: '8px', padding: '20px', background: 'white' }}>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '4px', textAlign: 'center', color: '#dc2626' }}>
                      Burdens
                    </h3>
                    <div style={{ fontSize: '1rem', textAlign: 'center', color: '#6b7280', marginBottom: '20px' }}>
                      (percentiles)
                    </div>
                    <BurdenBenefitsChart 
                      type="burdens" 
                      housingBurdenPercentile={housingBurdenPercentile}
                      onPercentileCalculated={onBurdenPercentileCalculated}
                    />
                  </div>
                )}
                {isOverviewChartEnabled('equity_index', 'benefits') && (
                  <div style={{ border: '2px solid #10b981', borderRadius: '8px', padding: '20px', background: 'white' }}>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '4px', textAlign: 'center', color: '#10b981' }}>
                      Benefits
                    </h3>
                    <div style={{ fontSize: '1rem', textAlign: 'center', color: '#6b7280', marginBottom: '20px' }}>
                      (percentiles)
                    </div>
                    <BurdenBenefitsChart 
                      type="benefits"
                      onPercentileCalculated={onBenefitsPercentileCalculated}
                    />
                  </div>
                )}
              </div>

              {showLivabilityRankingsChart && (
                <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '16px', background: 'white' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#0f172a' }}>
                      Livability Rankings ({rankingYear})
                    </h3>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', color: '#334155' }}>
                        Geography
                        <select
                          value={rankingGeographyType}
                          onChange={(e) => setRankingGeographyType(e.target.value as 'City' | 'County')}
                          style={{ border: '1px solid #cbd5e1', borderRadius: '6px', padding: '4px 8px' }}
                        >
                          <option value="City">Cities</option>
                          <option value="County">Counties</option>
                        </select>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', color: '#334155' }}>
                        Year
                        <select
                          value={rankingYear}
                          onChange={(e) => setRankingYear(Number(e.target.value))}
                          style={{ border: '1px solid #cbd5e1', borderRadius: '6px', padding: '4px 8px' }}
                        >
                          {rankingAvailableYears.map((year) => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', color: '#334155' }}>
                        Rows
                        <select
                          value={rankingRowCount}
                          onChange={(e) =>
                            setRankingRowCount(
                              e.target.value === 'all'
                                ? 'all'
                                : (Number(e.target.value) as RankingRowCount)
                            )
                          }
                          style={{ border: '1px solid #cbd5e1', borderRadius: '6px', padding: '4px 8px' }}
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                          <option value="all">All</option>
                        </select>
                      </label>
                    </div>
                  </div>

                  <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '12px' }}>
                    Formula matches Overview card: (Benefits + (100 - Burdens)) / 2. Years listed only when viable data exists.
                  </div>

                  {rankingLoading && (
                    <div style={{ color: '#64748b', fontSize: '0.95rem' }}>Loading rankings...</div>
                  )}
                  {rankingError && !rankingLoading && (
                    <div style={{ color: '#b91c1c', fontSize: '0.95rem' }}>{rankingError}</div>
                  )}

                  {!rankingLoading && !rankingError && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <h4 style={{ margin: '0 0 8px 0', color: '#166534' }}>Highest</h4>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                          <thead>
                            <tr style={{ background: '#f8fafc' }}>
                              <th style={{ textAlign: 'left', padding: '6px', borderBottom: '1px solid #e2e8f0' }}>#</th>
                              <th style={{ textAlign: 'left', padding: '6px', borderBottom: '1px solid #e2e8f0' }}>Location</th>
                              <th style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid #e2e8f0' }}>Score</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rankingTopRows.map((row) => (
                              <tr
                                key={`top-${row.locationName}`}
                                style={{
                                  backgroundColor:
                                    selectedLocation &&
                                    row.locationName.toLowerCase() === selectedLocation.toLowerCase()
                                      ? '#dbeafe'
                                      : 'transparent'
                                }}
                              >
                                <td style={{ padding: '6px', borderBottom: '1px solid #f1f5f9' }}>{row.rank}</td>
                                <td style={{ padding: '6px', borderBottom: '1px solid #f1f5f9' }}>{row.locationName}</td>
                                <td style={{ padding: '6px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 600 }}>{row.score.toFixed(1)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div>
                        <h4 style={{ margin: '0 0 8px 0', color: '#991b1b' }}>Lowest</h4>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                          <thead>
                            <tr style={{ background: '#f8fafc' }}>
                              <th style={{ textAlign: 'left', padding: '6px', borderBottom: '1px solid #e2e8f0' }}>#</th>
                              <th style={{ textAlign: 'left', padding: '6px', borderBottom: '1px solid #e2e8f0' }}>Location</th>
                              <th style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid #e2e8f0' }}>Score</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rankingBottomRows.map((row) => (
                              <tr
                                key={`bottom-${row.locationName}`}
                                style={{
                                  backgroundColor:
                                    selectedLocation &&
                                    row.locationName.toLowerCase() === selectedLocation.toLowerCase()
                                      ? '#dbeafe'
                                      : 'transparent'
                                }}
                              >
                                <td style={{ padding: '6px', borderBottom: '1px solid #f1f5f9' }}>{row.rank}</td>
                                <td style={{ padding: '6px', borderBottom: '1px solid #f1f5f9' }}>{row.locationName}</td>
                                <td style={{ padding: '6px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 600 }}>{row.score.toFixed(1)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {showLivabilityOverTimeChart && (
                <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '16px', background: 'white', marginTop: '16px' }}>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '1.1rem', fontWeight: 600, color: '#0f172a' }}>
                    {selectedLocation} Livability Over Time
                  </h3>
                  <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '12px' }}>
                    Tracks total livability score with total burdens and total benefits over time.
                  </div>

                  {livabilityTrendLoading && (
                    <div style={{ color: '#64748b', fontSize: '0.95rem' }}>Loading livability trend...</div>
                  )}
                  {livabilityTrendError && !livabilityTrendLoading && (
                    <div style={{ color: '#b91c1c', fontSize: '0.95rem' }}>{livabilityTrendError}</div>
                  )}

                  {!livabilityTrendLoading && !livabilityTrendError && livabilityTrendFigure && (
                    <div style={{ display: 'grid', gap: '14px' }}>
                      <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px' }}>
                        <h4 style={{ margin: '0 0 8px 0', color: '#0f172a' }}>Overall Index and Totals</h4>
                        <div style={{ height: '280px' }}>
                          <ChartRendererSupabase
                            chartData={livabilityTrendFigure.totalsChartData}
                            chartOptions={livabilityTrendFigure.totalsChartOptions}
                            height="280px"
                            location={selectedLocation}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {additionalOverviewCharts.length > 0 && (
          <div className="tool-section tool-section-white">
            <div className="section-container">
              <h2 style={{ fontSize: '1.8rem', fontWeight: '400', marginBottom: '20px', textAlign: 'center' }}>
                {renderSampleTitle('Additional Overview Charts')}
              </h2>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                  gap: '16px',
                  marginBottom: '20px'
                }}
              >
                {additionalOverviewCharts.map((chart) => (
                  <div
                    key={chart.id}
                    onClick={() => handleNavigateWithCheck(`/${chart.pagePath}`)}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '16px',
                      background: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: '120px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#3b82f6';
                      e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                      {chart.label}
                    </h4>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>
                      Click to view the full chart in the {chart.pagePath.replace('-', ' ')} section.
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* Section 2: Buildings */}
        {isOverviewSectionEnabled('buildings') && (
          <div className="tool-section tool-section-white">
            <div className="section-container">
              <h2 style={{ fontSize: '1.8rem', fontWeight: '400', marginBottom: '20px', textAlign: 'center' }}>
                {renderSampleTitle('Buildings & Energy')}
              </h2>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '20px',
                marginBottom: '20px'
              }}>
              {isOverviewChartEnabled('buildings', 'electricity_consumption') && (
                <div 
                  onClick={() => handleNavigateWithCheck('/buildings')}
                  style={{ 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px', 
                    padding: '15px', 
                    background: 'white', 
                    minHeight: '300px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                    Electricity Consumption{elecChangeSince2005 !== null ? `: ${elecChangeSince2005 > 0 ? '+' : ''}${elecChangeSince2005.toFixed(1)}% ${elecChangeSince2005 > 0 ? 'Increase' : 'Decrease'}` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1 }}>
                    {elecStacked ? (
                      <ChartRendererSupabase 
                        chartData={elecStacked.chartData} 
                        chartOptions={elecStacked.chartOptions} 
                        height="250px" 
                      location={selectedLocation} 
                      />
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                        <p>Loading Electricity data...</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {isOverviewChartEnabled('buildings', 'natural_gas_consumption') && (
                <div 
                  onClick={() => handleNavigateWithCheck('/buildings')}
                  style={{ 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px', 
                    padding: '15px', 
                    background: 'white', 
                    minHeight: '300px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                    Natural Gas Consumption{gasChangeSince2005 !== null ? `: ${gasChangeSince2005 > 0 ? '+' : ''}${gasChangeSince2005.toFixed(1)}% ${gasChangeSince2005 > 0 ? 'Increase' : 'Decrease'}` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1 }}>
                    {gasStacked ? (
                      <ChartRendererSupabase 
                        chartData={gasStacked.chartData} 
                        chartOptions={gasStacked.chartOptions} 
                        height="250px" 
                      location={selectedLocation} 
                    />
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                        <p>Loading Natural Gas data...</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {isOverviewChartEnabled('buildings', 'cumulative_solar_capacity') && (
                <div 
                  onClick={() => handleNavigateWithCheck('/buildings')}
                  style={{ 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px', 
                    padding: '15px', 
                    background: 'white', 
                    minHeight: '300px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                    Cumulative Solar Capacity{cumulativeSolarCapacity !== null ? `: ${cumulativeSolarCapacity.toFixed(1)} MW` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1, width: '100%', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
                    {selectedGeofips ? (
                      <div style={{ height: '250px', width: '100%' }}>
                        <CumulativeSolarCapacityChart 
                      location={selectedLocation} 
                          geofips={selectedGeofips}
                      embedOnly={true}
                    />
                  </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                        <p>Loading Solar PV data...</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>
              <div style={{ textAlign: 'right', marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => handleNavigateWithCheck('/buildings')}
                  style={{
                    color: '#3b82f6',
                    textDecoration: 'none',
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  More Buildings & Energy Figures →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Section 3: Transportation */}
        {isOverviewSectionEnabled('transportation') && (
          <div className="tool-section tool-section-gray">
            <div className="section-container">
              <h2 style={{ fontSize: '1.8rem', fontWeight: '400', marginBottom: '20px', textAlign: 'center' }}>
                {renderSampleTitle('Transportation')}
              </h2>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '15px',
                marginBottom: '20px'
              }}>
              {isOverviewChartEnabled('transportation', 'vehicle_miles') && (
                <div 
                  onClick={() => handleNavigateWithCheck('/transportation')}
                  style={{ 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px', 
                    padding: '12px', 
                    background: 'white', 
                    minHeight: '420px',
                    height: '420px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95rem', fontWeight: '600', color: '#1f2937' }}>
                    Vehicle Miles{vmtChangeSince2005 !== null ? `: ${vmtChangeSince2005 > 0 ? '+' : ''}${vmtChangeSince2005.toFixed(1)}% ${vmtChangeSince2005 > 0 ? 'Increase' : 'Decrease'} since 2005` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', width: '100%', height: '100%', position: 'relative' }}>
                    <VMTChart 
                        location={selectedLocation} 
                      geofips={selectedGeofips || null}
                      chartView="total"
                      backcastingMethod="Combined"
                      emfac2005_2014Method="Combined"
                      emfacFleetVersion="2025"
                      embedOnly={true}
                      onMetricsCalculated={onVmtMetricsCalculated}
                      onDataCalculated={onVmtDataCalculated}
                />
              </div>
            </div>
              )}
              {isOverviewChartEnabled('transportation', 'mode_share') && (
                <div 
                  onClick={() => handleNavigateWithCheck('/transportation')}
                  style={{ 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px', 
                    padding: '12px', 
                    background: 'white', 
                    minHeight: '420px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95rem', fontWeight: '600', color: '#1f2937' }}>
                    Mode Share{privateVehiclePercent !== null ? `: ${privateVehiclePercent.toFixed(1)}% Private Vehicle` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1 }}>
                    <TransportationModeShare 
                        location={selectedLocation} 
                      geofips={selectedGeofips || null} 
                      embedOnly={true}
                      onModeShareCalculated={onModeShareCalculated}
                      />
                  </div>
                </div>
              )}
              {isOverviewChartEnabled('transportation', 'ev_fleet_adoption') && (
                <div 
                  onClick={() => handleNavigateWithCheck('/transportation')}
                  style={{ 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px', 
                    padding: '12px', 
                    background: 'white', 
                    minHeight: '420px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95rem', fontWeight: '600', color: '#1f2937' }}>
                    EV Fleet Adoption{evPercent !== null ? `: ${evPercent.toFixed(1)}% of light-duty vehicles` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1 }}>
                    <EVFleetAdoption 
                        location={selectedLocation} 
                      geofips={selectedGeofips || null} 
                      embedOnly={true}
                      onEvPercentCalculated={onEvPercentCalculated}
                      />
                  </div>
                </div>
              )}
              </div>
              <div style={{ textAlign: 'right', marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => handleNavigateWithCheck('/transportation')}
                  style={{
                    color: '#3b82f6',
                    textDecoration: 'none',
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  More Transportation Figures →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Section 4: Waste+ */}
        {isOverviewSectionEnabled('waste') && (
          <div className="tool-section tool-section-white">
            <div className="section-container">
              <h2 style={{ fontSize: '1.8rem', fontWeight: '400', marginBottom: '20px', textAlign: 'center' }}>
                {renderSampleTitle('Waste +')}
              </h2>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '20px',
                marginBottom: '20px'
              }}>
              {isOverviewChartEnabled('waste', 'waste_tonnage') && (
                <div 
                  onClick={() => handleNavigateWithCheck('/other')}
                  style={{ 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px', 
                    padding: '15px', 
                    background: 'white', 
                    minHeight: '500px',
                    height: '500px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                    Waste Tonnage{wasteChangeSince2005 !== null ? `: ${wasteChangeSince2005 > 0 ? '+' : ''}${wasteChangeSince2005.toFixed(1)}% ${wasteChangeSince2005 > 0 ? 'Increase' : 'Decrease'} since 2005` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1, width: '100%', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
                    <WasteTonnageChart 
                      location={selectedLocation}
                      embedOnly={true}
                      onWasteChangeCalculated={onWasteChangeCalculated}
                    />
                  </div>
                </div>
              )}
              {isOverviewChartEnabled('waste', 'landfill_emissions') && (
                <div 
                  onClick={() => handleNavigateWithCheck('/other')}
                  style={{ 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px', 
                    padding: '15px', 
                    background: 'white', 
                    minHeight: '500px',
                    height: '500px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                    Landfill Emissions{landfillEmissions2023 !== null ? `: ${landfillEmissions2023.toLocaleString()} tons CO₂e (2023)` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1, width: '100%', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
                    <LandfillEmissionsStackedChart 
                      location={selectedLocation} 
                      embedOnly={true}
                      onEmissionsCalculated={onLandfillEmissionsCalculated}
                    />
                  </div>
                </div>
              )}
              </div>
              <div style={{ textAlign: 'right', marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => handleNavigateWithCheck('/other')}
                  style={{
                    color: '#3b82f6',
                    textDecoration: 'none',
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  More Waste+ Figures →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Section 5: People & Equity - Charts Only */}
        {isOverviewSectionEnabled('people_equity') && (
          <div className="tool-section tool-section-gray">
            <div className="section-container">
              <h2 style={{ fontSize: '1.8rem', fontWeight: '400', marginBottom: '20px', textAlign: 'center' }}>
                {renderSampleTitle('People & Equity')}
              </h2>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '20px',
                marginBottom: '20px'
              }}>
              {isOverviewChartEnabled('people_equity', 'income_by_race') && (
                <div 
                  onClick={() => handleNavigateWithCheck('/people')}
                  style={{ 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px', 
                    padding: '15px', 
                    background: 'white', 
                    minHeight: '440px',
                    height: '440px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                    Income by Race{incomeDifferenceRatio !== null ? ` (${incomeDifferenceRatio.toFixed(1)}x difference)` : ''}
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1, width: '100%', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
                    <IncomeByRaceBar key={selectedLocation} location={selectedLocation} embedOnly={true} onIncomeDifference={setIncomeDifferenceRatio} />
                  </div>
                </div>
              )}
              {isOverviewChartEnabled('people_equity', 'housing_burden') && (
                <div 
                  onClick={() => handleNavigateWithCheck('/people')}
                  style={{ 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px', 
                    padding: '15px', 
                    background: 'white', 
                    minHeight: '440px',
                    height: '440px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                    Households Spending 35%+ of Income on Housing
                  </h4>
                  <div style={{ pointerEvents: 'none', flex: 1, width: '100%', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
                    <HousingBurdenSection location={selectedLocation} geofips={selectedGeofips ? String(selectedGeofips) : null} embedOnly={true} />
                  </div>
                </div>
              )}
              </div>
              <div style={{ textAlign: 'right', marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => handleNavigateWithCheck('/people')}
                  style={{
                    color: '#3b82f6',
                    textDecoration: 'none',
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  More People & Equity Figures →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Section 6: GHG Inventory */}
        {isOverviewSectionEnabled('ghg_inventory') && (
          <div className="tool-section tool-section-white">
            <div className="section-container">
              <h2 style={{ fontSize: '1.8rem', fontWeight: '400', marginBottom: '20px', textAlign: 'center' }}>
                {renderSampleTitle('GHG Inventory')}
              </h2>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '20px',
                marginBottom: '20px'
              }}>
              {isOverviewChartEnabled('ghg_inventory', 'sector_based_ghg') && (
                <div 
                  onClick={() => handleNavigateWithCheck('/ghg')}
                  style={{ 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px', 
                    padding: '15px', 
                    background: 'white', 
                    minHeight: '400px', 
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                    Sector-Based GHG Inventory
                  </h4>
                  {overviewVMTChartLoaded && vmtDataReady && vmtDataAvailable && ghgInventoryData ? (
                    <div style={{ height: '370px', pointerEvents: 'none', flex: 1, overflow: 'hidden' }}>
                      <SectorBasedGHGInventory 
                        location={selectedLocation} 
                        chartType={ghgChartType} 
                        setChartType={setGhgChartType} 
                        ghgData={ghgInventoryData} 
                      />
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#64748b', flex: 1 }}>
                      <p>{overviewVMTChartLoaded ? 'Loading Sector-Based GHG Inventory...' : 'Loading VMT chart data...'}</p>
                    </div>
                  )}
                </div>
              )}
              {isOverviewChartEnabled('ghg_inventory', 'consumption_based_ghg') && (
                <div 
                  onClick={() => handleNavigateWithCheck('/ghg')}
                  style={{ 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px', 
                    padding: '15px', 
                    background: 'white', 
                    minHeight: '400px', 
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                    Consumption-Based GHG Inventory
                  </h4>
                  {consumptionGHGData ? (
                    <div style={{ height: '370px', pointerEvents: 'none', flex: 1, overflow: 'hidden' }}>
                      <ConsumptionBasedGHGInventory 
                        location={selectedLocation} 
                        chartType={consumptionChartType} 
                        setChartType={setConsumptionChartType} 
                      />
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#64748b', flex: 1 }}>
                      <p>Loading Consumption-Based GHG Inventory...</p>
                    </div>
                  )}
                </div>
              )}
              </div>
              <div style={{ textAlign: 'right', marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => handleNavigateWithCheck('/ghg')}
                  style={{
                    color: '#3b82f6',
                    textDecoration: 'none',
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  More GHG Inventory Figures →
                </button>
              </div>
            </div>
          </div>
        )}

        {selectedLocation && (
          <>
            <CustomSectionsForPage pageId="overview" locationName={selectedLocation} />
            <CustomDatasetsForPage pageId="overview" locationName={selectedLocation} />
          </>
        )}
      </div>

      {/* Data Download Modal */}
      <DataDownloadModal
        isOpen={showDataDownload}
        onClose={() => setShowDataDownload(false)}
        location={selectedLocation || ''}
        geofips={selectedGeofips}
      />
    </div>
  );
};
